import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import { toast } from "sonner";
import "driver.js/dist/driver.css";
import "@/styles/tour.css";
import { createDemoTender } from "@/lib/api";

const WELCOME_KEY = "tour:welcome";
const WELCOME_STEP_KEY = "tour:welcome:step";

/**
 * Multi-page guided onboarding. The flow is a sequence of "stops".
 * Each stop pins to a specific route — when the user lands on that route
 * the driver.js popover appears. "Siguiente →" advances to the next stop;
 * if the next stop lives on a different route, the orchestrator navigates
 * there and the popover reappears as soon as the page mounts.
 *
 * Persistence:
 *   - localStorage[WELCOME_KEY] = "done" once finished/skipped.
 *   - localStorage[WELCOME_STEP_KEY] = index of the next stop to show
 *     while the tour is in progress (survives reloads).
 */
const STOPS = [
    {
        route: "/app",
        popover: {
            title: "👋 Bienvenido a LicitaIA",
            description:
                "Te enseño en 1 minuto cómo funciona la plataforma. Si lo prefieres, pulsa la X para saltarlo y explorar por tu cuenta.",
        },
    },
    {
        route: "/app",
        element: "[data-testid='dashboard-kpis']",
        popover: {
            title: "📊 Tu centro de mando",
            description:
                "Aquí ves cuántas licitaciones tienes en curso, presentadas, ganadas y el importe acumulado. Todo en tiempo real.",
        },
    },
    {
        route: "/app/buscar-plcsp",
        popover: {
            title: "🔍 Buscador de licitaciones",
            description:
                "Acceso al PLCSP del Estado + plataformas autonómicas en una sola búsqueda. Filtra, ordena e importa con un clic.",
        },
    },
    {
        route: "/app/licitaciones",
        popover: {
            title: "📁 Mis licitaciones",
            description:
                "Todas las licitaciones que importas o creas a mano viven aquí. La IA analiza los pliegos y genera anexos automáticamente.",
        },
    },
    {
        route: "/app/crm",
        popover: {
            title: "🏆 CRM de licitaciones",
            description:
                "Cada vez que pulses 'Presentar licitación', aparecerá aquí. Arrastra a Adjudicada/Perdida para registrar el resultado.",
        },
    },
    {
        route: "/app/memoria-tecnica",
        popover: {
            title: "✨ Memoria técnica con IA",
            description:
                "Sube los pliegos y la IA redacta la memoria respetando todas las restricciones formales (tipografía, páginas, secciones).",
        },
    },
    {
        route: "/app",
        popover: {
            title: "🚀 ¡Listo!",
            description:
                "Ya conoces lo esencial. En cada página tienes el botón flotante 'Ver tour' por si quieres revisar una funcionalidad concreta. Te ofrecemos también una licitación de demostración para que pruebes todo sin importar una real. ¡Mucha suerte!",
        },
        isFinal: true,
    },
];

function getStep() {
    try {
        const v = parseInt(localStorage.getItem(WELCOME_STEP_KEY) || "0", 10);
        return Number.isFinite(v) ? v : 0;
    } catch {
        return 0;
    }
}

function setStep(n) {
    try {
        localStorage.setItem(WELCOME_STEP_KEY, String(n));
    } catch {
        // ignore
    }
}

function markDone() {
    try {
        localStorage.setItem(WELCOME_KEY, "done");
        localStorage.removeItem(WELCOME_STEP_KEY);
        // Avoid bombarding the user with the per-page tours right after the
        // global welcome — they can still re-launch any of them from the
        // floating "Ver tour" button.
        for (const k of [
            "tour:dashboard",
            "tour:plcsp",
            "tour:licitaciones",
            "tour:crm",
            "tour:memoria-standalone",
            "tour:cumplimentar-anexos",
            "tour:analytics",
            "tour:perfil-empresa",
        ]) {
            localStorage.setItem(k, "done");
        }
    } catch {
        // ignore
    }
}

function hasFinished() {
    try {
        return localStorage.getItem(WELCOME_KEY) === "done";
    } catch {
        return false;
    }
}

function isActive() {
    try {
        return localStorage.getItem(WELCOME_STEP_KEY) != null && !hasFinished();
    } catch {
        return false;
    }
}

/**
 * <WelcomeTourBootstrap /> — mount once inside the authenticated layout.
 * It listens to route changes, opens driver.js whenever the current route
 * matches the pending welcome stop, and handles navigation between stops.
 */
export function WelcomeTourBootstrap({ enabled = true }) {
    const navigate = useNavigate();
    const location = useLocation();
    const driverRef = useRef(null);
    const stopTimers = useRef([]);

    const close = useCallback(() => {
        stopTimers.current.forEach(clearTimeout);
        stopTimers.current = [];
        if (driverRef.current) {
            try {
                driverRef.current.destroy();
            } catch {
                // already destroyed
            }
            driverRef.current = null;
        }
    }, []);

    const launch = useCallback(
        (stopIndex) => {
            const stop = STOPS[stopIndex];
            if (!stop) {
                markDone();
                close();
                return;
            }
            // If the stop is on another route, navigate there first.
            if (stop.route && location.pathname !== stop.route) {
                setStep(stopIndex);
                navigate(stop.route);
                return;
            }
            // If a selector is required but absent, retry briefly (lets the
            // page hydrate KPIs etc.) then fall back to a centered popover.
            const needsSelector = !!stop.element;
            const selectorMissing =
                needsSelector && !document.querySelector(stop.element);

            const open = () => {
                close();
                const isLast = stopIndex === STOPS.length - 1;
                let advanced = false; // guard against double-advance
                const advance = (delta) => {
                    if (advanced) return;
                    advanced = true;
                    const next = stopIndex + delta;
                    if (next < 0) {
                        // can't go before the first stop
                        launch(0);
                        return;
                    }
                    if (next >= STOPS.length) {
                        markDone();
                        close();
                        navigate("/app");
                        // If the just-finished step is the final one, offer
                        // the demo tender as an opt-in CTA via a sticky toast.
                        if (stop.isFinal) {
                            toast(
                                "🎁 ¿Quieres probar con una licitación de demostración?",
                                {
                                    description:
                                        "Te creamos un expediente ficticio con análisis IA, escandallo, checklist y memoria técnica ya rellenos para que pruebes todo en 2 minutos.",
                                    duration: 25000,
                                    action: {
                                        label: "Crear demo",
                                        onClick: async () => {
                                            try {
                                                toast.loading(
                                                    "Creando tu licitación demo…",
                                                    { id: "demo-create" },
                                                );
                                                const t = await createDemoTender();
                                                toast.success(
                                                    "Licitación demo creada — explórala ahora",
                                                    {
                                                        id: "demo-create",
                                                        duration: 6000,
                                                    },
                                                );
                                                navigate(
                                                    `/app/licitacion/${t.id}`,
                                                );
                                            } catch (e) {
                                                toast.error(
                                                    e?.response?.data?.detail ||
                                                        "No se pudo crear la demo. Pruébalo más tarde.",
                                                    { id: "demo-create" },
                                                );
                                            }
                                        },
                                    },
                                },
                            );
                        }
                        return;
                    }
                    setStep(next);
                    launch(next);
                };
                const d = driver({
                    showProgress: true,
                    allowClose: true,
                    smoothScroll: true,
                    overlayColor: "rgba(15, 23, 42, 0.7)",
                    popoverClass: "licitaia-tour",
                    stagePadding: 8,
                    stageRadius: 14,
                    nextBtnText: isLast ? "Empezar 🚀" : "Siguiente →",
                    prevBtnText: "← Atrás",
                    doneBtnText: isLast ? "Empezar 🚀" : "Siguiente →",
                    progressText: `Paso ${stopIndex + 1} de ${STOPS.length}`,
                    onCloseClick: () => {
                        if (advanced) return;
                        advanced = true;
                        markDone();
                        close();
                    },
                    onNextClick: () => advance(+1),
                    onPrevClick: () => advance(-1),
                    steps: [
                        {
                            element: selectorMissing
                                ? undefined
                                : stop.element,
                            popover: stop.popover,
                        },
                    ],
                });
                driverRef.current = d;
                d.drive();
            };

            if (selectorMissing) {
                // Retry up to ~1.5s for the selector to appear.
                let tries = 0;
                const id = setInterval(() => {
                    tries += 1;
                    if (document.querySelector(stop.element) || tries >= 6) {
                        clearInterval(id);
                        open();
                    }
                }, 250);
                stopTimers.current.push(id);
            } else {
                open();
            }
        },
        [location.pathname, navigate, close],
    );

    // React to route changes: if the welcome tour is active and the current
    // route matches the pending stop, launch it (after a small mount delay).
    // First-mount: if the user has never seen the tour, bootstrap step 0.
    useEffect(() => {
        if (!enabled) return;
        if (hasFinished()) return;
        if (!isActive()) {
            // Only auto-start when the user is on the dashboard page —
            // avoids hijacking a deep-link to e.g. a tender detail page.
            if (location.pathname === "/app") {
                setStep(0);
            } else {
                return;
            }
        }
        const idx = getStep();
        const stop = STOPS[idx];
        if (!stop) return;
        if (stop.route !== location.pathname) return;
        const t = setTimeout(() => launch(idx), 700);
        stopTimers.current.push(t);
        return () => {
            clearTimeout(t);
        };
    }, [enabled, location.pathname, launch]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => close();
    }, [close]);

    return null;
}

/**
 * Programmatic helpers exposed for the "Volver a ver el tour de bienvenida"
 * button in the user menu (future improvement).
 */
export function startWelcomeTour(navigate) {
    setStep(0);
    try {
        localStorage.removeItem(WELCOME_KEY);
    } catch {
        // ignore
    }
    if (typeof navigate === "function") navigate("/app");
}

export function resetWelcomeTour() {
    try {
        localStorage.removeItem(WELCOME_KEY);
        localStorage.removeItem(WELCOME_STEP_KEY);
    } catch {
        // ignore
    }
}

export function welcomeTourFinished() {
    return hasFinished();
}

/**
 * Trigger that fires once when a freshly-authenticated user lands on the
 * app for the first time. Returns true if the tour was kicked off.
 */
export function maybeStartWelcomeTourForNewUser() {
    if (hasFinished()) return false;
    if (isActive()) return true;
    setStep(0);
    return true;
}
